import React, { useState, useEffect } from 'react';
import { Importer, ImporterField } from 'react-csv-importer';
import Modal from 'components/elements/Modal';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import useSaveCSV from './crud/useSaveCSV';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

// theme CSS for React CSV Importer
import 'react-csv-importer/dist/index.css';
import CSVRoleSelectionForm from './csvRoleSelectionForm';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { colours } from 'utils/constants';
import { CSVLink } from 'react-csv';
import { Box, CircularProgress } from '@mui/material';
import { getTimestamp } from 'utils/helpers';
import { toast } from 'react-toastify';
import { useAuth } from 'services/auth-service';

const ImportCSVSchema = yup.object().shape({
  buildingUuid: yup.string().required('Building is required'),
  accountUuid: yup.string().required('Account is required'),
  integrationUuid: yup.string().required('Integration is required')
});

// Define the headers explicitly
const headers = [
  { label: 'FirstName', key: 'firstName' },
  { label: 'LastName', key: 'lastName' },
  { label: 'Email', key: 'email' },
  { label: 'PhoneNumber', key: 'phoneNumber' },
  { label: 'Organization', key: 'organization' }
];

// Define the data rows for sample csv
const data = [
  {
    firstName: 'John',
    lastName: 'Doe',
    email: 'johndoe@example.com',
    phoneNumber: '2234567891',
    organization: 'B-Line Test Org'
  },
  {
    firstName: 'Jane',
    lastName: 'Smith',
    email: 'janesmith@example.com',
    phoneNumber: '5987654321',
    organization: 'B-Line Test Org'
  }
];

export default function ImportCSV({
  showModal,
  setShowModal,
  entityName,
  importQuery,
  queryName
}: any) {
  const {
    state: { user }
  } = useAuth();
  const [resultCSV, setResultCSV] = useState<any>();
  const [loading, setLoading] = useState(false);

  const methods = useForm({
    defaultValues: {
      buildingUuid: '',
      accountUuid: '',
      integrationUuid: ''
    },
    resolver: yupResolver(ImportCSVSchema)
  });

  const saveCSVMutation = useSaveCSV({
    importQuery,
    queryName,
    onSuccess: (result: any) => {
      setResultCSV(result);
      setLoading(false);
    },
    onError: (error: any) => {
      setLoading(false);
      if (error.response.status === 400 && user.role === 'ORG') {
        toast.error('User is only allowed to import occupants in their own organization', {
          autoClose: 15000
        });
      } else {
        toast.error(
          'An error occurred while importing the CSV file. Please try again later. If the problem persists, contact support.'
        );
      }
    }
  });

  /**
   * This function creates a downloadable link for a CSV file and triggers a download when clicked.
   */
  const downloadFile = () => {
    const timestamp = getTimestamp();
    const url = window.URL.createObjectURL(new Blob([resultCSV]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `import_result_${timestamp}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const { watch, formState } = methods;
  const { isValid } = formState;
  const { integrationUuid, accountUuid, buildingUuid } = watch();

  const [isImporterDisabled, setImporterDisabled] = useState(true);

  useEffect(() => {
    setImporterDisabled(!integrationUuid || !accountUuid || !buildingUuid || !isValid);
  }, [integrationUuid, accountUuid, buildingUuid, isValid]);

  return (
    <Modal
      isOpen={showModal}
      onRequestClose={() => {
        setShowModal(false);
      }}
      title={entityName}
    >
      <CSVRoleSelectionForm Controller={Controller} methods={methods} />
      <div className="w-full">
        <div className="mb-4">
          <Typography
            variant="body1"
            gutterBottom
            sx={{
              color: colours.formLabelGrey,
              fontSize: '0.875rem',
              fontWeight: 600
            }}
          >
            To ensure a smooth import, please download and use our template
          </Typography>
          <Button
            variant="outlined"
            sx={{
              backgroundColor: 'white',
              borderColor: colours.seafoam,
              borderWidth: '1.5px',
              color: colours.seafoam,
              textTransform: 'none',
              fontWeight: '650',
              '&:hover': {
                backgroundColor: colours.seafoam,
                borderColor: colours.seafoam,
                borderWidth: '1.5px',
                color: 'white'
              }
            }}
          >
            <CSVLink
              data={data}
              headers={headers}
              filename="bline_template.csv"
              enclosingCharacter={''}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              Download .csv Template
            </CSVLink>
          </Button>
        </div>
        <br />
        {isImporterDisabled ? (
          <Box
            sx={{
              border: '2px dashed #cccccc',
              borderRadius: '4px',
              padding: '20px',
              textAlign: 'center',
              color: '#fa6b5b',
              backgroundColor: '#f9f9f9',
              height: '150px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography variant="body1">
              Please fill in all required fields to proceed with the import.
            </Typography>
          </Box>
        ) : (
          <Importer
            dataHandler={async () => {
              // required, receives a list of parsed objects based on defined fields and user column mapping;
              // may be called several times if file is large
              // (if this callback returns a promise, the widget will wait for it before parsing more data)

              // mock timeout to simulate processing
              await new Promise((resolve) => setTimeout(resolve, 500));
            }}
            chunkSize={10000} // optional, internal parsing chunk size in bytes
            defaultNoHeader={false} // optional, keeps "data has headers" checkbox off by default
            restartable={false} // optional, lets user choose to upload another file when import is complete
            onStart={({}) => {
              // optional, invoked when user has mapped columns and started import
            }}
            onComplete={({ file }) => {
              // optional, invoked right after import is done (but user did not dismiss/reset the widget yet)

              setLoading(true); // Start loading when CSV generation starts
              saveCSVMutation.mutate({
                accountUuid: accountUuid,
                buildingUuid: buildingUuid,
                integrationUuid: integrationUuid,
                row: file
              });
            }}
            onClose={() => {
              setShowModal(false);
            }}
          >
            <div className="w-[55rem]">
              <ImporterField name="organization" label="Organization" />
              <ImporterField name="firstName" label="First Name" />
              <ImporterField name="lastName" label="Last Name" />
              <ImporterField name="email" label="Email" />
              <ImporterField name="phoneNumber" label="Phone Number" />
              <ImporterField name="links" label="Links" optional />
              <ImporterField name="facilityCode" label="Facility Code" optional />
              <ImporterField name="badgeId" label="Badge Id" optional />
            </div>
          </Importer>
        )}
        {loading && (
          <Box display="flex" justifyContent="flex-end" alignItems="center" mt={2}>
            <CircularProgress size={24} sx={{ color: colours.seafoam }} />
            <Typography variant="body2" ml={1}>
              Generating Result CSV...
            </Typography>
          </Box>
        )}
        {resultCSV && (
          <Box display="flex" justifyContent="flex-end" alignItems="center" mt={2}>
            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              sx={{
                backgroundColor: colours.seafoam,
                color: 'white',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: colours.seafoam,
                  color: 'white'
                }
              }}
              onClick={downloadFile}
            >
              Download Result
            </Button>
          </Box>
        )}
      </div>
    </Modal>
  );
}
